import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { TextField, MenuItem, Select, FormControl, InputLabel, FormHelperText, CircularProgress, IconButton, InputAdornment, Chip, Snackbar, Alert } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff, ArrowBack, AddCircle, Edit, Delete, Security, CheckCircle, Close, Folder } from '@mui/icons-material';
import { adminService } from '../../../shared/services/adminService';
import { MENU_ITEMS } from '../../../shared/constants/menu';

export const AddEditAdmin: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isAdd = !id;

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [role, setRole] = useState('admin');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });

    const [adminDetails, setAdminDetails] = useState<any>({});
    const [currentAdmin, setCurrentAdmin] = useState<any>({});

    const [menuPermissions, setMenuPermissions] = useState<string[]>([]);
    const menuList = (() => {
        const seen = new Set<string>();
        return MENU_ITEMS.filter(item => {
            if (seen.has(item.permission)) return false;
            seen.add(item.permission);
            return true;
        }).map(item => ({ id: item.permission, name: item.title }));
    })();

    const actions = [
        { id: 'view', name: 'View' },
        { id: 'add', name: 'Add' },
        { id: 'update', name: 'Update' },
        { id: 'delete', name: 'Delete' }
    ];

    useEffect(() => {
        const adminDetail = localStorage.getItem('userDetail');
        if (adminDetail) {
            try {
                const admin = JSON.parse(adminDetail);
                setCurrentAdmin({
                    ...admin,
                    _id: admin._id ?? admin.id,
                    role: admin.role || 'admin'
                });
            } catch (error) {
                console.error('Error parsing current admin:', error);
            }
        }

        if (id) {
            loadAdmin(id);
        } else {
            // Add mode - default permissions
            setMenuPermissions(['dashboard', 'dashboard.view']);
        }
    }, [id]);

    const loadAdmin = async (adminId: string) => {
        setIsLoadingAdmin(true);
        try {
            const res = await adminService.getDetails(adminId);
            const admin = res.data?.admin || res.data?.result || res.data || res;

            if (!admin) {
                setAlert({ show: true, type: 'error', message: 'Admin data not found' });
                return;
            }

            setAdminDetails(admin);
            setName(admin.name || '');
            setEmail(admin.email || '');
            setRole(admin.role || 'admin');

            // Normalize menu permissions - merging permissions and menuPermissions if they are separate
            const rawMenu = admin.menuPermissions || admin.menu_permission || admin.menuPermission || [];
            let normalizedMenu: string[] = [];

            if (Array.isArray(rawMenu)) {
                normalizedMenu = rawMenu;
            } else if (typeof rawMenu === 'string') {
                normalizedMenu = rawMenu.split(',').map(x => x.trim());
            }

            setMenuPermissions(normalizedMenu);
        } catch (err: any) {
            console.error('Error loading admin:', err);
            setAlert({
                show: true,
                type: 'error',
                message: err.response?.data?.message || err.message || 'Error loading admin details'
            });
        } finally {
            setIsLoadingAdmin(false);
        }
    };

    const toggleAction = (menuId: string, actionId: string) => {
        const permissionStr = actionId === 'view' ? menuId : `${menuId}.${actionId}`;
        const current = [...menuPermissions];
        let next: string[];

        if (current.includes(permissionStr)) {
            next = current.filter(p => p !== permissionStr);
            // If removing view, also remove all other actions for this menu
            if (actionId === 'view') {
                next = next.filter(p => !p.startsWith(`${menuId}.`));
            }
        } else {
            next = [...current, permissionStr];
            // If adding any action, ensure view (menu base) is also added
            if (actionId !== 'view' && !next.includes(menuId)) {
                next.push(menuId);
            }
        }
        setMenuPermissions(next);
    };

    const toggleAllRow = (menuId: string) => {
        const rowPermissions = [menuId, `${menuId}.add`, `${menuId}.update`, `${menuId}.delete`];
        const hasAll = rowPermissions.every(p => menuPermissions.includes(p));

        if (hasAll) {
            setMenuPermissions(menuPermissions.filter(p => !rowPermissions.includes(p)));
        } else {
            const next = [...menuPermissions];
            rowPermissions.forEach(p => {
                if (!next.includes(p)) next.push(p);
            });
            setMenuPermissions(next);
        }
    };

    const toggleAllPermissions = () => {
        if (menuPermissions.includes('*')) {
            setMenuPermissions([]);
        } else {
            setMenuPermissions(['*']);
        }
    };

    const isActionSelected = (menuId: string, actionId: string): boolean => {
        if (menuPermissions.includes('*')) return true;
        const permissionStr = actionId === 'view' ? menuId : `${menuId}.${actionId}`;
        return menuPermissions.includes(permissionStr);
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!name || name.trim() === '') {
            newErrors.name = 'Name is required.';
        }

        if (!email || email.trim() === '') {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter valid email.';
        }

        if (isAdd && (!password || password.trim() === '')) {
            newErrors.password = 'Password is required.';
        } else if (password && password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters.';
        }

        if (isAdd && (!passwordConfirmation || passwordConfirmation.trim() === '')) {
            newErrors.passwordConfirmation = 'Confirm Password is required.';
        } else if (password && passwordConfirmation && password !== passwordConfirmation) {
            newErrors.passwordConfirmation = 'Password do not match.';
        }

        if (isAdd && !role) {
            newErrors.role = 'Role is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);

        try {
            // Compute global permissions for backend compatibility
            const permission = menuPermissions.includes('*')
                ? { add: true, update: true, delete: true }
                : {
                    add: menuPermissions.some(p => p.endsWith('.add')),
                    update: menuPermissions.some(p => p.endsWith('.update')),
                    delete: menuPermissions.some(p => p.endsWith('.delete'))
                };

            const body: any = {
                name,
                email,
                role: role || 'admin',
                permission,
                menuPermission: menuPermissions
            };

            if (password && password.trim() !== '') {
                body.password = password;
            }

            if (isAdd) {
                await adminService.add(body);
            } else {
                await adminService.edit(adminDetails._id || id, body);
            }

            setAlert({ show: true, type: 'success', message: isAdd ? 'Admin added successfully' : 'Admin updated successfully' });
            setTimeout(() => {
                navigate('/admin');
            }, 1000);
        } catch (err: any) {
            console.error('Error saving admin:', err);
            let errorMessage = 'Unknown error occurred';
            if (err.response?.data?.data?.errors) {
                errorMessage = Object.values(err.response.data.data.errors).join(', ');
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            setAlert({ show: true, type: 'error', message: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    const showPermissions = isAdd || (adminDetails.role !== 'super_admin' && adminDetails._id !== currentAdmin._id);

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="-ml-4">
                        <Link to="/admin" className="back-link">
                            <ArrowBack className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </Link>
                    </div>
                    <div className="title text-2xl sm:text-5xl">
                        {isAdd ? 'Add' : 'Edit'} Admin
                    </div>

                    {/* Form */}
                    <div className="form-card">
                        {isLoadingAdmin ? (
                            <div className="loading-container flex flex-col items-center justify-center py-20">
                                <CircularProgress size={50} />
                                <p className="mt-4 text-gray-600 font-medium">Loading admin details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type === 'error' ? 'error' : 'success'}
                                        className="mb-8 rounded-xl"
                                        onClose={() => setAlert({ ...alert, show: false })}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="form space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <TextField
                                            label="Name"
                                            placeholder="Enter full name"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                if (errors.name) setErrors({ ...errors, name: '' });
                                            }}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            fullWidth
                                            required
                                        />

                                        {/* Email */}
                                        <TextField
                                            label="Email"
                                            placeholder="Enter email address"
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (errors.email) setErrors({ ...errors, email: '' });
                                            }}
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            fullWidth
                                            required
                                            autoComplete="off"
                                        />

                                        {/* Role */}
                                        <FormControl fullWidth error={!!errors.role}>
                                            <InputLabel>Role</InputLabel>
                                            <Select
                                                value={role}
                                                onChange={(e) => {
                                                    setRole(e.target.value);
                                                    if (errors.role) setErrors({ ...errors, role: '' });
                                                }}
                                                label="Role"
                                                disabled={!isAdd && adminDetails.role === 'super_admin'}
                                            >
                                                <MenuItem value="admin">Admin</MenuItem>
                                                {currentAdmin.role === 'super_admin' && (
                                                    <MenuItem value="super_admin">Super Admin</MenuItem>
                                                )}
                                            </Select>
                                            {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                                        </FormControl>

                                        {/* Password */}
                                        <TextField
                                            label={isAdd ? "Password" : "New Password (leave blank to keep current)"}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Minimum 8 characters"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                if (errors.password) setErrors({ ...errors, password: '' });
                                            }}
                                            error={!!errors.password}
                                            helperText={errors.password}
                                            fullWidth
                                            required={isAdd}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <TextField
                                            label="Confirm Password"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="Confirm password"
                                            value={passwordConfirmation}
                                            onChange={(e) => {
                                                setPasswordConfirmation(e.target.value);
                                                if (errors.passwordConfirmation) setErrors({ ...errors, passwordConfirmation: '' });
                                            }}
                                            error={!!errors.passwordConfirmation}
                                            helperText={errors.passwordConfirmation}
                                            fullWidth
                                            required={isAdd || !!password}
                                            autoComplete="off"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </div>

                                    {/* Admin Permissions Table */}
                                    {showPermissions && (
                                        <div className="mt-8">
                                            <div className="flex items-center justify-between mb-4 px-2">
                                                <div className="flex items-center gap-2">
                                                    <Security className="text-[#0EA5E9]" />
                                                    <h3 className="text-xl font-bold text-gray-800">Menu Permissions</h3>
                                                </div>
                                                <Chip
                                                    label="All Permissions"
                                                    onClick={toggleAllPermissions}
                                                    icon={menuPermissions.includes('*') ? <CheckCircle sx={{ color: '#fff !important' }} /> : undefined}
                                                    sx={{
                                                        backgroundColor: menuPermissions.includes('*') ? '#0EA5E9' : '#f3f4f6',
                                                        color: menuPermissions.includes('*') ? '#fff' : '#4b5563',
                                                        fontWeight: 600,
                                                        px: 1,
                                                        '&:hover': {
                                                            backgroundColor: menuPermissions.includes('*') ? '#0284C7' : '#e5e7eb'
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm">
                                                <table className="w-full text-left border-collapse">
                                                    <thead>
                                                        <tr className="bg-gray-50 border-b border-gray-200">
                                                            <th className="p-4 font-semibold text-gray-700">Menu Name</th>
                                                            {actions.map(action => (
                                                                <th key={action.id} className="p-4 font-semibold text-gray-700 text-center">{action.name}</th>
                                                            ))}
                                                            <th className="p-4 font-semibold text-gray-700 text-center">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {menuList.map((menu) => (
                                                            <tr key={menu.id} className="border-b border-gray-100 hover:bg-blue-50/30 transition-colors">
                                                                <td className="p-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-white">
                                                                            <Folder className="text-gray-500 w-5 h-5" />
                                                                        </div>
                                                                        <span className="font-medium text-gray-800">{menu.name}</span>
                                                                    </div>
                                                                </td>
                                                                {actions.map(action => (
                                                                    <td key={action.id} className="p-4 text-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="w-5 h-5 rounded border-gray-300 text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer"
                                                                            checked={isActionSelected(menu.id, action.id)}
                                                                            onChange={() => toggleAction(menu.id, action.id)}
                                                                            disabled={menuPermissions.includes('*')}
                                                                        />
                                                                    </td>
                                                                ))}
                                                                <td className="p-4 text-center">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleAllRow(menu.id)}
                                                                        disabled={menuPermissions.includes('*')}
                                                                        className={`text-xs font-bold px-3 py-1 rounded-full border transition-all ${[menu.id, `${menu.id}.add`, `${menu.id}.update`, `${menu.id}.delete`].every(p => menuPermissions.includes(p))
                                                                            ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]'
                                                                            : 'text-[#0EA5E9] border-[#0EA5E9] hover:bg-blue-50'
                                                                            }`}
                                                                    >
                                                                        All
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/admin')}
                                            sx={{
                                                px: 4,
                                                backgroundColor: '#f3f4f6',
                                                color: '#4b5563',
                                                '&:hover': { backgroundColor: '#e5e7eb' }
                                            }}
                                        >
                                            Cancel
                                        </AppButton>

                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            disabled={isLoading}
                                            loading={isLoading}
                                            sx={{ px: 6 }}
                                        >
                                            {isLoading ? 'Saving...' : (isAdd ? 'Create Admin' : 'Update Admin')}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, show: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ top: '80px !important' }}
            >
                <Alert severity={alert.type === 'error' ? 'error' : 'success'} variant="filled" className="rounded-xl shadow-lg">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
